import React, { useEffect, useRef, useCallback } from "react";
import { useGlobalSearch } from "../context/GlobalSearchContext";
import GlobalSearchResults from "./GlobalSearchResults";

/**
 * GlobalSearchBar Component
 * Search input for the header with keyboard shortcuts and results dropdown
 */
export default function GlobalSearchBar() {
  const { isOpen, openSearch, closeSearch, dismissDropdown, query, setQuery } = useGlobalSearch();
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (isOpen) {
          closeSearch();
        } else {
          openSearch();
        }
      }
      
      // Escape to dismiss dropdown (keep query intact so user can continue typing)
      if (e.key === "Escape" && isOpen) {
        dismissDropdown();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, openSearch, closeSearch, dismissDropdown]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeSearch();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, closeSearch]);

  // Handle input change with debounce built into the component
  const handleInputChange = useCallback((e) => {
    setQuery(e.target.value);
  }, [setQuery]);

  // Handle focus to open
  const handleFocus = useCallback(() => {
    openSearch();
  }, [openSearch]);

  // Handle arrow key navigation to reopen dropdown when closed
  // Just reopens the dropdown - selection stays where it was
  const handleKeyDown = useCallback((e) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      openSearch();
      // Don't move selection here - just reopen and show current position
      // The GlobalSearchResults component will handle subsequent arrow key presses
    }
  }, [isOpen, openSearch]);

  return (
    <div ref={containerRef} className="relative flex-1 max-w-md mx-4">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-4 w-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder="Search..."
          className="w-full pl-10 pr-12 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
        />
        {/* Keyboard shortcut hint */}
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      </div>

      {/* Results Dropdown */}
      {isOpen && <GlobalSearchResults inputRef={inputRef} />}
    </div>
  );
}
